<template>
  <div class="details-page">
    <b-modal
      v-model="showWarning"
      id="terminate-job-modal"
      title="Warning"
      hide-header-close
    >
      <template #modal-header>
        <h5 class="modal-title">Warning</h5>
        <b-button class="close" @click="showWarning = false" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </b-button>
      </template>
      <p class="my-4">
        Are you sure you want to terminate this job? This action cannot be
        undone.
      </p>
      <template #modal-footer>
        <b-button variant="secondary" @click="showWarning = false"
          >Cancel</b-button
        >
        <b-button variant="danger" @click="handleTerminateJob"
          >Terminate</b-button
        >
      </template>
    </b-modal>

    <b-modal
      v-model="showAppendModal"
      id="append-posts-modal"
      title="Append Posts"
      hide-header-close
    >
      <template #modal-header>
        <div class="append-modal-header">
          <h5 class="modal-title mb-0">Append Posts</h5>
          <b-button class="close" @click="closeAppendModal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </b-button>
        </div>
      </template>

      <div class="append-modal-body">
        <p class="append-modal-subtitle mb-3">
          Upload a ZIP file to append new posts to this job queue.
        </p>
        <vue-dropzone
          id="append-dropzone"
          ref="appendDropzone"
          :options="appendDropzoneOptions"
          @vdropzone-file-added="handleAppendFileAdded"
          @vdropzone-removed-file="handleAppendFileRemoved"
          class="append-dropzone"
        >
          <div class="dropzone-custom-content">
            <h3 class="dropzone-custom-title">Drop ZIP file here</h3>
            <div class="subtitle">or click to select</div>
          </div>
        </vue-dropzone>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="closeAppendModal">Cancel</b-button>
        <b-button
          variant="primary"
          :disabled="!appendFile || appendingPosts"
          @click="handleAppendPosts"
        >
          <b-spinner small v-if="appendingPosts" class="mr-2"></b-spinner>
          {{ appendingPosts ? "Appending..." : "Append" }}
        </b-button>
      </template>
    </b-modal>

    <!-- Error Modal -->
    <b-modal
      v-model="showErrorModal"
      id="post-error-modal"
      title="Post Error Details"
      hide-header-close
      size="lg"
    >
      <template #modal-header>
        <h5 class="modal-title">Post Error Details</h5>
        <b-button
          class="close"
          @click="showErrorModal = false"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </b-button>
      </template>
      <div v-if="selectedErrorPost" class="error-details">
        <div class="detail-row">
          <span class="detail-label">File Path:</span>
          <span class="detail-value">{{ selectedErrorPost.filePath }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Post Status:</span>
          <span class="detail-value">
            <b-badge variant="danger">{{ selectedErrorPost.status }}</b-badge>
          </span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Error Message:</span>
          <span class="detail-value error-message">{{
            selectedErrorPost.error
          }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Posted At:</span>
          <span class="detail-value">{{
            formatDate(selectedErrorPost.timestamp)
          }}</span>
        </div>
        <div v-if="selectedErrorPost.postTime" class="detail-row">
          <span class="detail-label">Scheduled Post Time:</span>
          <span class="detail-value">{{
            formatDate(selectedErrorPost.postTime)
          }}</span>
        </div>
      </div>
      <template #modal-footer>
        <b-button variant="primary" @click="showErrorModal = false"
          >Close</b-button
        >
      </template>
    </b-modal>

    <!-- Loading Spinner -->
    <template v-if="loading">
      <div class="text-center">
        <b-spinner style="width: 5rem; height: 5rem" type="grow"></b-spinner>
      </div>
    </template>

    <!-- Main Content -->
    <template v-else>
      <section class="automation-workbench">
        <header class="job-hero-card">
          <div class="job-pager-buttons">
            <b-button
              class="pager-button"
              @click="navigateAutomation('previous')"
              :disabled="navigating"
              v-b-tooltip.hover
              title="Previous job"
            >
              <b-spinner v-if="navigating" small></b-spinner>
              <b-icon v-else icon="chevron-left"></b-icon>
            </b-button>
            <b-button
              class="pager-button"
              @click="navigateAutomation('next')"
              :disabled="navigating"
              v-b-tooltip.hover
              title="Next job"
            >
              <b-spinner v-if="navigating" small></b-spinner>
              <b-icon v-else icon="chevron-right"></b-icon>
            </b-button>
          </div>

          <div class="job-hero-copy">
            <div class="job-kicker">
              <span
                class="status-bubble"
                :class="'status-bubble-' + (automation.status || '').toLowerCase()"
              >
                <span class="state-dot"></span>
                {{ automation.status }}
              </span>
              <span>{{ automation.postType || "Automation" }}</span>
              <span>{{ formatDate(automation.createdAt) }} UK</span>
            </div>
            <h1>
              {{ automation.name || automation.pageName || "Scheduled Job" }}
            </h1>
            <div class="job-context-row">
              <span>
                <b-icon icon="clock" aria-hidden="true"></b-icon>
                {{ automation.intervalMin }} - {{ automation.intervalMax }} minute cadence
              </span>
              <a
                class="page-visit-link"
                :href="'https://facebook.com/' + automation.pageId"
                target="_blank"
                rel="noopener noreferrer"
              >
                <b-icon icon="link-45deg" aria-hidden="true"></b-icon>
                <span>{{ automation.pageName || automation.pageId }}</span>
                <b-icon icon="box-arrow-up-right" aria-hidden="true"></b-icon>
              </a>
            </div>
          </div>

          <div class="job-action-cluster">
            <b-button
              class="job-command action-neutral"
              v-if="
                automation?.status !== 'Completed' &&
                automation?.status !== 'Terminated'
              "
              @click="toggleStatus"
              :disabled="loadingStausButton"
            >
              <b-spinner v-if="loadingStausButton" small></b-spinner>
              <b-icon
                v-else
                :icon="automation?.status === 'Running' ? 'pause-fill' : 'play-fill'"
              />
              <span>{{ automation?.status === "Running" ? "Pause" : "Resume" }}</span>
            </b-button>

            <b-button
              class="job-command action-danger"
              @click="showTermitateModal"
              :disabled="
                loadingStausButton ||
                automation?.status === 'Terminated' ||
                automation?.status === 'Completed'
              "
            >
              <b-spinner v-if="loadingStausButton" small></b-spinner>
              <b-icon v-else icon="x-octagon"></b-icon>
              <span>{{ automation?.status === "Terminated" ? "Terminated" : "Terminate" }}</span>
            </b-button>

            <b-button
              class="job-command action-primary"
              @click="showAppendModal = true"
              :disabled="automation?.status === 'Terminated'"
            >
              <b-icon icon="plus-circle"></b-icon>
              <span>Add Posts</span>
            </b-button>
          </div>
        </header>

        <section class="job-detail-band">
          <div class="detail-band-intro">
            <span>Job setup</span>
            <strong>{{ automation.postType || "Post" }} queue</strong>
          </div>

          <div class="detail-band-grid">
            <div class="detail-band-item">
              <span>Job ID</span>
              <strong>{{ automation.jobId }}</strong>
            </div>
            <div class="detail-band-item">
              <span>Page ID</span>
              <strong>{{ automation.pageId }}</strong>
            </div>
            <div class="detail-band-item">
              <span>Caption source</span>
              <strong>{{ getCaptionSourceLabel(automation) }}</strong>
            </div>
            <div class="detail-band-item">
              <span>Created</span>
              <strong>{{ formatDate(automation.createdAt) }}</strong>
            </div>
          </div>
        </section>

        <section class="posts-panel">
          <div class="posts-panel-head">
            <div>
              <h2>Associated Posts</h2>
              <p>{{ totalPosts }} posts in this automation queue.</p>
            </div>
            <div class="posts-tools">
              <div class="post-health-inline">
                <span
                  v-for="(count, status) in statusCounts"
                  :key="status"
                  class="post-count-pill"
                >
                  {{ status }} <strong>{{ count }}</strong>
                </span>
              </div>
              <div class="sort-controls">
                <b-form-select
                  v-model="sortBy"
                  :options="sortOptions"
                  size="sm"
                  class="modern-select"
                />
                <b-form-select
                  v-model="sortOrder"
                  :options="orderOptions"
                  size="sm"
                  class="modern-select"
                />
              </div>
            </div>
          </div>

          <b-table
            hover
            small
            :items="posts"
            :fields="postFields"
            :busy="loading"
            class="modern-table"
            responsive
          >
            <template v-slot:cell(contentId)="data">
              <div class="mono-cell" :title="data.item.contentId">
                {{ data.item.contentId || "-" }}
              </div>
            </template>
            <template v-slot:cell(filePath)="data">
              <div class="file-path-cell" :title="data.item.filePath">
                {{ data.item.filePath }}
              </div>
            </template>
            <template v-slot:cell(status)="data">
              <span
                class="post-status"
                :class="'post-status-' + (data.item.status || '').toLowerCase()"
              >
                {{ data.item.status }}
              </span>
            </template>
            <template v-slot:cell(postTime)="data">
              {{ formatDate(data.item?.postTime) }}
            </template>
            <template v-slot:cell(timestamp)="data">
              {{ formatDate(data.item.timestamp) }}
            </template>
            <template v-slot:cell(collab)="data">
              <template
                v-if="['Invited', 'Clicked'].includes(data.item.collabStatus)"
              >
                <a
                  @click="handleUpdateCollabStatus(data.item._id)"
                  target="_blank"
                  :href="'https://' + data.item.collabLink"
                >
                  Link
                </a>
              </template>
              <div v-else>-</div>
            </template>
            <template v-slot:cell(error)="data">
              <template v-if="data.item.status === 'Failed' && data.item.error">
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="showErrorDetails(data.item)"
                  class="error-button"
                >
                  <b-icon icon="exclamation-circle" class="mr-1"></b-icon>
                  View Error
                </b-button>
              </template>
              <div v-else>-</div>
            </template>
          </b-table>

          <div class="pagination-wrapper">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalPosts"
              :per-page="pageSize"
              align="center"
              @input="fetchPosts"
              first-text="«"
              prev-text="‹"
              next-text="›"
              last-text="»"
              size="md"
              class="custom-pagination"
            />
          </div>
        </section>
      </section>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import vueDropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

export default {
  components: {
    vueDropzone,
  },
  data() {
    const basePostFields = [
      { key: "filePath", label: "File Name" },
      { key: "status", label: "Status" },
      { key: "postTime", label: "Approx. Post Time" },
      { key: "error", label: "Error" },
    ];
    return {
      id: null,
      loading: true,
      automation: {},
      loadingStausButton: false,
      navigating: false,
      showWarning: false,
      showAppendModal: false,
      appendFile: null,
      appendingPosts: false,
      appendDropzoneOptions: {
        url: "dummy",
        thumbnailWidth: 150,
        maxFilesize: 10000,
        addRemoveLinks: true,
        acceptedFiles: ".zip",
        autoProcessQueue: false,
        maxFiles: 1,
      },
      basePostFields,
      postFields: [...basePostFields],
      currentPage: 1,
      pageSize: 10,
      totalPosts: 0,
      sortBy: "timestamp",
      sortOrder: "desc",
      posts: [],
      showErrorModal: false,
      selectedErrorPost: null,
      sortOptions: [
        { value: "timestamp", text: "Timestamp" },
        { value: "status", text: "Status" },
      ],
      orderOptions: [
        { value: "asc", text: "Asc" },
        { value: "desc", text: "Desc" },
      ],
      statusCounts: {},
    };
  },
  watch: {
    sortBy() {
      this.fetchPosts();
    },
    sortOrder() {
      this.fetchPosts();
    },
  },
  mounted() {
    this.id = this.$route.params.id;
    this.getData();
  },

  methods: {
    async handleUpdateCollabStatus(postId) {
      try {
        this.loading = true;
        const response = await axios.post("/api/post/collab-clicked", {
          postId,
        });

        if (response.data.success) {
          this.$toast.success("Collab status updated successfully!");
          this.posts = this.posts.map((p) => {
            if (p._id === postId) p.collabStatus = "Clicked";
            return p;
          });
        }
      } catch (e) {
        console.log("Error occured while updating collab status");
        console.error(e);
        $toast.error("Error occured while updating collab status");
      } finally {
        this.loading = false;
      }
    },
    closeAppendModal() {
      this.showAppendModal = false;
      this.appendFile = null;
      if (this.$refs.appendDropzone) {
        this.$refs.appendDropzone.removeAllFiles(true);
      }
    },
    handleAppendFileAdded(file) {
      this.appendFile = file;
      if (
        this.$refs.appendDropzone &&
        this.$refs.appendDropzone.dropzone.files.length > 1
      ) {
        const files = this.$refs.appendDropzone.dropzone.files;
        this.$refs.appendDropzone.removeFile(files[0]);
      }
    },
    handleAppendFileRemoved() {
      this.appendFile = null;
    },
    async handleAppendPosts() {
      if (!this.appendFile) {
        this.$toast.error("Please select a ZIP file");
        return;
      }

      try {
        this.appendingPosts = true;
        const formData = new FormData();
        formData.append("file", this.appendFile);

        const isPhoto = this.automation.postType === "Photo";
        const endpoint = isPhoto
          ? `/append-posts/${this.automation.jobId}`
          : `/append-reels/${this.automation.jobId}`;

        await axios.post(endpoint, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        this.$toast.success("Posts appended successfully");
        this.closeAppendModal();
        this.currentPage = 1;
        await this.getData();
      } catch (error) {
        const message =
          error?.response?.data?.error || "Failed to append posts";
        this.$toast.error(message);
      } finally {
        this.appendingPosts = false;
      }
    },

    async getData() {
      this.loading = true;
      const response = await axios.post("/api/automations/detail", {
        _id: this.id,
      });
      this.automation = response.data.data;
      this.id = response.data.currentJobId;
      this.loading = false;
      this.postFields = [...this.basePostFields];

      if (this.automation.postType === "Reel") {
        this.postFields = [{ key: "contentId", label: "Content ID" }, ...this.postFields];
      }

      if (this.automation.collabId && this.automation.collabId !== "null") {
        this.postFields.push({ key: "collab", label: "Collab Link" });
        this.postFields.push("collabStatus");
      }

      this.fetchPosts(); // fetch post list
    },

    async fetchPosts() {
      this.loading = true;
      try {
        const res = await axios.post(
          `/api/job/posts?page=${this.currentPage}&pageSize=${this.pageSize}&sortBy=${this.sortBy}&order=${this.sortOrder}`,
          {
            jobId: this.automation.jobId,
          }
        );

        this.posts = res.data.data;
        this.totalPosts = res.data.totalPosts;
        this.statusCounts = res.data.statusCounts || {};
      } catch (err) {
        console.error("Failed to fetch posts", err);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      try {
        return moment(date).format("DD-MM-YYYY HH:mm:ss");

        const _date = new Date(date).toLocaleString();

        if (_date === "Invalid Date") {
          return "Calculating";
        } else {
          return _date;
        }
      } catch (e) {
        return "-";
      }
    },
    getCaptionSourceLabel(job) {
      const hasHashtags = Boolean(
        job?.hashtagPool && job.hashtagPool !== "null"
      );

      if (job?.fileNameAsDescription && hasHashtags) {
        return "Filename + hashtags";
      }

      if (job?.fileNameAsDescription) {
        return "Filename";
      }

      return hasHashtags ? "Hashtags only" : "Disabled";
    },
    isImage(filePath) {
      return filePath && filePath.match(/\.(jpeg|jpg|gif|png|webp)$/i);
    },

    showTermitateModal() {
      this.showWarning = true;
    },

    cancelTerminate() {
      this.showWarning = false;
    },

    handleTerminateJob() {
      this.loadingStausButton = true;

      axios
        .post(`/terminate-job/${this.automation.jobId}`)
        .then((res) => {
          if (res.data.success) {
            this.$toast.success("Job Terminated!");
            this.automation.status = "Terminated";
          }
        })
        .catch((e) => {
          this.$toast.error("Failed to Terminate Job!");
        })
        .finally((_) => {
          this.loadingStausButton = false;
          this.getData();
          this.showWarning = false;
        });
    },

    toggleStatus() {
      this.loadingStausButton = true;

      let url;
      const { status, jobId } = this.automation;

      if (status === "Running") {
        url = `/pause-job/${jobId}`;
      } else {
        url = `/resume-job/${jobId}`;
      }

      axios
        .post(url)
        .then((res) => {
          if (res.data.success) {
            this.$toast.success(`Job ${res.data.status}!`);
            this.automation.status = res.data.status;
          }
        })
        .catch((e) => {
          this.$toast.error("Failed to change Job Status!");
        })
        .finally((_) => {
          this.loadingStausButton = false;
          this.getData();
        });
    },

    async navigateAutomation(direction) {
      try {
        this.navigating = true;
        const response = await axios.post("/api/automations/navigate", {
          direction,
          currentJobId: this.id,
        });

        if (response.data.success) {
          // Update the route to the new job
          this.id = response.data.data._id;
          this.automation = response.data.data;
          this.$router.push({
            name: "AutomationDetail",
            params: { id: response.data.data._id },
          });
          this.$toast.success(`Navigated to ${direction} automation!`);
          // Reload posts for the new job
          this.currentPage = 1;
          this.fetchPosts();
        }
      } catch (error) {
        console.error("Error navigating automations:", error);
        if (error.response?.data?.message) {
          this.$toast.error(error.response.data.message);
        } else {
          this.$toast.error("Failed to navigate automations!");
        }
      } finally {
        this.navigating = false;
      }
    },

    showErrorDetails(post) {
      this.selectedErrorPost = post;
      this.showErrorModal = true;
    },

    getBadgeVariant(status) {
      const variants = {
        Published: "success",
        Failed: "danger",
        Pending: "warning",
        Clicked: "info",
        Invited: "info",
      };
      return variants[status] || "secondary";
    },
  },
};
</script>

<style scoped>
.details-page {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  background: #f7f9fc;
  border-radius: 12px;
  padding: 1rem;
  color: #162033;
}

.details-page *,
.details-page *::before,
.details-page *::after {
  box-sizing: border-box;
}

.details-page .card {
  max-width: 100%;
}

.details-page .d-flex.justify-content-between {
  gap: 0.75rem;
}

.details-page .page-link {
  overflow-wrap: anywhere;
}

.automation-workbench {
  display: grid;
  gap: 0.85rem;
}

.job-hero-card,
.job-detail-band,
.posts-panel {
  background: #ffffff;
  border: 1px solid #e1e8f0;
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(16, 24, 40, 0.055);
}

.job-hero-card {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: center;
  overflow: hidden;
  padding: 1rem 1.1rem;
}

.job-hero-card::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 5px;
  background: linear-gradient(180deg, #19b36c, #0b66d8);
}

.job-pager-buttons {
  display: inline-flex;
  gap: 0.4rem;
}

.pager-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f8fbff !important;
  border: 1px solid #d9e4f0 !important;
  border-radius: 999px;
  color: #304156 !important;
  box-shadow: none !important;
}

.pager-button:hover {
  color: #0b66d8 !important;
  border-color: #9fc6f5 !important;
  background: #f1f7ff !important;
}

.job-hero-copy {
  min-width: 0;
}

.job-kicker,
.job-context-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  color: #60738a;
  font-size: 0.86rem;
  font-weight: 750;
}

.job-kicker > span:not(.status-bubble) + span::before {
  content: "";
  display: inline-block;
  width: 4px;
  height: 4px;
  margin-right: 0.5rem;
  border-radius: 999px;
  background: #b5c2d0;
  vertical-align: middle;
}

.status-bubble {
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
  padding: 0.24rem 0.55rem;
  border-radius: 999px;
  background: #eef8f3;
  color: #19734b;
  border: 1px solid #cbecda;
}

.status-bubble-paused {
  background: #fff7ed;
  color: #9a5b13;
  border-color: #fed7aa;
}

.status-bubble-completed,
.status-bubble-published {
  background: #eef8f3;
  color: #19734b;
  border-color: #cbecda;
}

.status-bubble-terminated,
.status-bubble-failed {
  background: #fff1f2;
  color: #b4232c;
  border-color: #fecdd3;
}

.state-dot {
  width: 8px;
  height: 8px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: currentColor;
  box-shadow: 0 0 0 5px rgba(25, 115, 75, 0.1);
}

.job-hero-copy h1 {
  margin: 0.45rem 0 0;
  color: #101828;
  font-size: clamp(1.45rem, 2vw, 2rem);
  font-weight: 850;
  letter-spacing: 0;
  line-height: 1.1;
  overflow-wrap: anywhere;
}

.job-context-row {
  margin-top: 0.65rem;
  font-size: 0.93rem;
}

.job-context-row > span,
.page-visit-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.page-visit-link {
  max-width: 100%;
  color: #0b66d8 !important;
  background: #f3f8ff;
  border: 1px solid #cfe4ff;
  border-radius: 999px;
  padding: 0.28rem 0.55rem;
  text-decoration: none;
}

.page-visit-link span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-visit-link:hover {
  background: #e9f3ff;
  border-color: #0b66d8;
  text-decoration: none;
}

.job-action-cluster {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  min-width: 168px;
}

.job-command {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  border-radius: 999px;
  border: 1px solid #dce5ef !important;
  font-weight: 800;
  box-shadow: none !important;
  transition: transform 0.16s ease, border-color 0.16s ease, background 0.16s ease;
}

.job-command:hover:not(:disabled) {
  transform: translateY(-1px);
}

.action-primary {
  background: #0b66d8 !important;
  border-color: #0b66d8 !important;
  color: #ffffff !important;
}

.action-neutral {
  background: #ffffff !important;
  color: #26374a !important;
}

.action-danger {
  background: #fff3f3 !important;
  border-color: #ffd0d0 !important;
  color: #bf2a2a !important;
}

.job-detail-band {
  display: grid;
  grid-template-columns: 170px minmax(0, 1fr);
  align-items: stretch;
  overflow: hidden;
}

.detail-band-intro {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.2rem;
  padding: 0.95rem 1rem;
  background: #f7fafc;
  border-right: 1px solid #e1e8f0;
}

.detail-band-intro span,
.detail-band-item span {
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 750;
}

.detail-band-intro strong,
.detail-band-item strong {
  color: #101828;
  font-size: 0.98rem;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.detail-band-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.detail-band-item {
  min-width: 0;
  padding: 0.95rem 1rem;
  border-right: 1px solid #eef2f6;
}

.detail-band-item:last-child {
  border-right: none;
}

.detail-band-item strong {
  display: block;
  margin-top: 0.3rem;
}

.posts-panel {
  padding: 0.95rem;
}

.posts-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.8rem;
}

.posts-panel-head h2 {
  margin: 0;
  color: #101828;
  font-size: 1.1rem;
  font-weight: 850;
}

.posts-panel-head p {
  margin: 0.2rem 0 0;
  color: #64748b;
  font-size: 0.9rem;
}

.posts-tools {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.post-health-inline {
  display: inline-flex;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.post-count-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 34px;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  border: 1px solid #e3e9f1;
  background: #fbfcfe;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 750;
}

.post-count-pill strong {
  color: #101828;
  font-size: 0.95rem;
}

@media (max-width: 1180px) {
  .job-hero-card {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .job-action-cluster {
    grid-column: 1 / -1;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    min-width: 0;
  }

  .job-detail-band {
    grid-template-columns: 1fr;
  }

  .detail-band-intro {
    border-right: none;
    border-bottom: 1px solid #e1e8f0;
  }

  .detail-band-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .detail-band-item:nth-child(2n) {
    border-right: none;
  }

  .detail-band-item:nth-child(n + 3) {
    border-top: 1px solid #eef2f6;
  }
}

@media (max-width: 767.98px) {
  .job-hero-card,
  .posts-panel-head {
    display: flex;
    flex-direction: column;
  }

  .job-pager-buttons,
  .job-action-cluster,
  .posts-tools,
  .sort-controls {
    width: 100%;
  }

  .job-action-cluster,
  .detail-band-grid {
    grid-template-columns: 1fr;
  }

  .detail-band-item {
    border-right: none;
    border-top: 1px solid #eef2f6;
  }
}

.append-modal-body {
  padding: 0.25rem 0;
}

.append-modal-header {
  width: 100%;
  display: flex;
  align-items: center;
}

.append-modal-header .close {
  margin-left: auto;
}

.append-modal-subtitle {
  color: #64748b;
  font-size: 0.95rem;
}

.append-dropzone {
  border: 2px dashed #cbd5e1 !important;
  border-radius: 10px !important;
  background: #f8fafc !important;
  min-height: 170px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.2s ease;
}

.append-dropzone:hover {
  border-color: #0077ff !important;
  background: #f1f5f9 !important;
}

.file-path-cell {
  max-width: 780px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mono-cell {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.86rem;
  color: #334155;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 991.98px) {
  .details-page .d-flex.justify-content-between {
    flex-wrap: wrap;
  }
}

@media (max-width: 767.98px) {
  .details-page {
    padding: 0.75rem;
  }

  .details-page .sort-controls {
    width: 100%;
    margin-top: 0.5rem;
  }

  .details-page .modern-select {
    flex: 1 1 0;
    min-width: 0;
  }
}

.modern-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  margin: 1rem 0;
  border: 1px solid #e2e8f0;
}

.modern-table >>> thead th {
  background: #f8fafc !important;
  color: #334155 !important;
  font-weight: 600;
  padding: 0.85rem 1rem;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0;
  border-bottom: 1px solid #e2e8f0;
}

.modern-table >>> tbody td {
  padding: 0.85rem 1rem;
  color: #1e293b;
  font-size: 0.95rem;
  border-bottom: 1px solid #f1f5f9;
  background: white;
  vertical-align: middle;
}

.modern-table >>> tbody tr:hover {
  background-color: #f8fafc;
}

.modern-table >>> tbody tr:last-child td {
  border-bottom: none;
}

/* Pagination Styling */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  padding: 0;
}

.custom-pagination {
  display: flex;
  gap: 0.25rem;
  background: #f8f8f8 !important;
  padding: 10px;
}

.custom-pagination >>> .page-item .page-link {
  border: none;
  padding: 0.5rem 0.75rem;
  color: #64748b;
  font-weight: 500;
  background: transparent;
  border-radius: 6px;
  transition: all 0.2s ease;
  margin: 5px;
}

.custom-pagination >>> .page-item:not(.disabled):hover .page-link {
  background: #f1f5f9;
  color: #0077ff;
}

.custom-pagination >>> .page-item.active .page-link {
  background: #0077ff;
  color: white;
}

.custom-pagination >>> .page-item.disabled .page-link {
  color: #cbd5e1;
  background: transparent;
}

/* Remove existing pagination styles */
.my-pagination-wrapper {
  background: none;
  padding: 0;
}

.pagination {
  background: none;
  padding: 0;
}

.sort-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  border-radius: 8px;
}

.modern-select {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.5rem 2rem 0.5rem 1rem;
  font-size: 0.9rem;
  color: #1e293b;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 0.75rem;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.modern-select:hover {
  border-color: #0077ff;
}

.modern-select:focus {
  outline: none;
  border-color: #0077ff;
  box-shadow: 0 0 0 3px rgba(0, 119, 255, 0.1);
}

/* Error Modal Styles */
.error-details {
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 600;
  color: #1e293b;
  min-width: 150px;
}

.detail-value {
  color: #475569;
  word-break: break-word;
  flex: 1;
  text-align: right;
}

.error-message {
  color: #dc2626;
  font-weight: 500;
  background: #fee2e2;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
}

.error-button {
  white-space: nowrap;
}

.error-button i {
  margin-right: 0.25rem;
}

.status-summary-row {
  border-radius: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  border: 1px solid #e2e8f0;
}

.post-status {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  border: 1px solid transparent;
}

.post-status-published,
.post-status-uploaded {
  color: #166534;
  background: #dcfce7;
  border-color: #bbf7d0;
}

.post-status-pending {
  color: #92400e;
  background: #fef3c7;
  border-color: #fde68a;
}

.post-status-failed {
  color: #991b1b;
  background: #fee2e2;
  border-color: #fecaca;
}

.status-badge {
  padding: 0.6rem 1rem !important;
  font-size: 0.9rem !important;
  font-weight: 600 !important;
  text-transform: capitalize;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: inline-block !important;
  color: white !important;
}

.status-summary-row >>> .badge-success {
  background-color: #28a745 !important;
}

.status-summary-row >>> .badge-danger {
  background-color: #dc3545 !important;
}

.status-summary-row >>> .badge-warning {
  background-color: #ffc107 !important;
  color: #333 !important;
}

.status-summary-row >>> .badge-info {
  background-color: #17a2b8 !important;
}

.status-summary-row >>> .badge-secondary {
  background-color: #6c757d !important;
}
</style>

<style>
.pagination {
  background: #fff;
  /* width: 100% !important; */
  padding: 5px 10px;
  border-radius: 5px;
}
</style>
